import React, { createContext, useContext, useState, useEffect } from "react";
import { BASE_API_URL } from "../utils/app-constants";
import axios from "axios";
import { toast } from "react-toastify";

export const CartContext = createContext({});

export function CartProvider(props) {
  const [items, setItems] = useState([]);
  const [isSelfCollection, setIsSelfCollection] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [cookingMethodCosts, setCookingMethodCosts] = useState({});

  useEffect(() => {
    axios
      .get(`${BASE_API_URL}public/settings`)
      .then((res) => {
        const settings = res.data?.returned_resultset || [];
        const costs = {};
        const KEY_TO_CODE = {
          DEEP_FRIED: "deep_fried",
          GRILLED: "grilled",
        };
        settings.forEach((s) => {
          const code = KEY_TO_CODE[s.key];
          if (code) costs[code] = parseFloat(s.value);
        });
        setCookingMethodCosts(costs);
      })
      .catch((err) =>
        console.error("Unable to fetch cooking method costs", err),
      );
  }, []);

  console.log(coupon, "cp");

  function addItemToCart(product, qty) {
    setItems((prevItems) => {
      const item = prevItems.find(
        (item) => item.product.id == product.id && !item.cooking_method,
      );
      if (!item) {
        return [
          ...prevItems,
          {
            qty: qty ? qty : 1,
            product,
            deep_fried: false,
            discountedTotal: product?.discounted_price?.toFixed(0) * qty,
            totalPrice: product.unit_value * qty,
          },
        ];
      } else {
        return prevItems.map((item) => {
          if (item.product.id == product.id && !item.cooking_method) {
            item.qty = qty;
            item.totalPrice = product.unit_value * qty;
            item.discountedTotal = product?.discounted_price * qty;
          }
          return item;
        });
      }
    });
    toast.success("Item added to basket");
  }
  function setCookingMethodQuantity(productId, methodCode, qty) {
    setItems((prevItems) => {
      const productLines = prevItems.filter((i) => i.product.id === productId);
      const product = productLines[0]?.product;
      if (!product) return prevItems;

      const totalQty = productLines.reduce((sum, i) => sum + i.qty, 0);
      const currentByMethod = {};
      productLines.forEach((i) => {
        currentByMethod[i.cooking_method || "none"] = i.qty;
      });
      const currentQty = currentByMethod[methodCode] || 0;
      const delta = qty - currentQty;
      const newByMethod = { ...currentByMethod };
      if (delta > 0) {
        let need = delta;
        if (methodCode !== "none") {
          const available = currentByMethod.none || 0;
          const borrow = Math.min(need, available);
          newByMethod.none = available - borrow;
          need -= borrow;
        }
        newByMethod[methodCode] = currentQty + delta - need;
      } else if (delta < 0) {
        // Shrinking this method — return the freed units to Fresh/"none",
        // unless we're editing "none" itself.
        const freed = -delta;
        newByMethod[methodCode] = Math.max(0, qty);
        if (methodCode !== "none") {
          newByMethod.none = (currentByMethod.none || 0) + freed;
        }
      }

      const newLines = Object.entries(newByMethod)
        .filter(([, q]) => q > 0)
        .map(([code, q]) => ({
          qty: q,
          product,
          cooking_method: code === "none" ? null : code,
          totalPrice: product.unit_value * q,
          discountedTotal: product?.discounted_price
            ? product.discounted_price * q
            : undefined,
        }));

      // FIX: walk the original array once, dropping this product's lines
      // and splicing newLines in at the position they first appeared,
      // instead of filtering-then-appending (which pushed the edited
      // product to the bottom of the cart every time).
      const result = [];
      let inserted = false;
      prevItems.forEach((item) => {
        if (item.product.id === productId) {
          if (!inserted) {
            result.push(...newLines);
            inserted = true;
          }
          // skip remaining old lines for this product, already replaced
        } else {
          result.push(item);
        }
      });
      if (!inserted) result.push(...newLines);

      return result;
    });
  }

  function getCookingFee() {
    return items
      .filter((item) => item.cooking_method)
      .reduce(
        (sum, item) =>
          sum + item.qty * (cookingMethodCosts[item.cooking_method] || 0),
        0,
      );
  }
  function toggleDeepFried(productId) {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.product.id === productId
          ? { ...item, deep_fried: !item.deep_fried }
          : item,
      ),
    );
  }

  function removeItem(id) {
    return setItems(() => items?.filter((item) => item?.product.id !== id));
  }

  function updateCartItem(updateditems) {
    setItems(() => updateditems);
  }
  function getDeepFryFee() {
    return items
      .filter((item) => item.deep_fried)
      .reduce(
        (sum, item) => sum + item.qty * (cookingMethodCosts.deep_fried || 0),
        0,
      );
  }
  function getItemsCount() {
    return items.length;
  }

  function getTotalPrice() {
    const itemsTotal = items.reduce(
      (sum, item) => sum + (item?.discountedTotal || item.totalPrice),
      0,
    );
    const deliveryFee = isSelfCollection ? 0 : itemsTotal < 4000 ? 400 : 0;
    return itemsTotal + getCookingFee() + deliveryFee;
  }

  return (
    <CartContext.Provider
      value={{
        items,
        setItems,
        getItemsCount,
        addItemToCart,
        getTotalPrice,
        updateCartItem,
        removeItem,
        toggleDeepFried,
        getCookingFee,
        getDeepFryFee,
        cookingMethodCosts,
        setCookingMethodQuantity,
        setIsSelfCollection,
        isSelfCollection,
        coupon,
        setCoupon,
      }}
    >
      {props.children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
